import React, { useEffect, useState } from 'react';
import { Target, Zap, Coins, Medal } from 'lucide-react';
import { api } from '../lib/api';
import { BRAND } from '../brand';

interface Position {
  position: number;
  total: number;
  /** En Dahus llega null en perfiles ajenos: el saldo es privado. */
  value: number | null;
}

interface Rankings {
  rating: Position | null;
  efficiency: Position | null;
  /** Preguntas que faltan para entrar en el ranking de eficiencia. */
  efficiencyMissing: number;
  dahus: Position;
}

/**
 * Puesto de una cuenta en cada ranking de la plataforma. Lo usan el perfil
 * propio y el público. `refreshKey` vuelve a pedirlo cuando algo cambia.
 */
export const RankingPositions: React.FC<{ userId: string; own?: boolean; refreshKey?: unknown }> = ({
  userId,
  own,
  refreshKey,
}) => {
  const [data, setData] = useState<Rankings | null>(null);

  useEffect(() => {
    let cancelled = false;
    void api
      .get<{ rankings: Rankings }>(`/users/${encodeURIComponent(userId)}/rankings`)
      .then((r) => !cancelled && setData(r.rankings))
      .catch(() => !cancelled && setData(null));
    return () => {
      cancelled = true;
    };
  }, [userId, refreshKey]);

  if (!data) return null;

  return (
    <div className="surface p-5 space-y-3">
      <p className="text-ink-400 text-xs uppercase tracking-wider flex items-center gap-1.5">
        <Medal className="w-4 h-4" />
        {own ? 'Tu posición en los rankings' : 'Posición en los rankings'}
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Card
          tone="bg-sky-500/15 text-sky-400"
          icon={<Target className="w-4 h-4" />}
          label="D-Rating"
          pos={data.rating}
          value={data.rating ? `${data.rating.value} puntos` : ''}
          empty={own ? 'Juega una partida para entrar.' : 'Aún no ha jugado.'}
        />
        <Card
          tone="bg-success-500/15 text-success-400"
          icon={<Zap className="w-4 h-4" />}
          label="Eficiencia"
          pos={data.efficiency}
          value={data.efficiency ? `${data.efficiency.value} de 100` : ''}
          empty={`${own ? 'Te faltan' : 'Le faltan'} ${data.efficiencyMissing} preguntas para entrar.`}
        />
        <Card
          tone="bg-ember-500/15 text-ember-400"
          icon={<Coins className="w-4 h-4" />}
          label={BRAND.currencyName}
          pos={data.dahus}
          value={data.dahus.value === null ? '' : `${data.dahus.value.toLocaleString('es')} ${BRAND.currencyName}`}
          empty=""
        />
      </div>
    </div>
  );
};

const MEDALS = ['🥇', '🥈', '🥉'];

const Card: React.FC<{
  icon: React.ReactNode;
  label: string;
  pos: Position | null;
  value: string;
  empty: string;
  tone: string;
}> = ({ icon, label, pos, value, empty, tone }) => {
  const medal = pos && pos.position <= 3 ? MEDALS[pos.position - 1] : null;
  return (
    <div
      className={`relative rounded-xl border p-4 overflow-hidden ${
        medal ? 'border-ember-500/60 bg-gradient-to-br from-ember-500/15 to-transparent' : 'border-ink-700 bg-ink-850'
      }`}
    >
      <div className="flex items-center gap-2 mb-2">
        <span className={`w-8 h-8 rounded-lg flex items-center justify-center ${tone}`}>{icon}</span>
        <p className="text-ink-400 text-xs font-medium">{label}</p>
        {medal && (
          <span className="font-emoji text-2xl leading-none ml-auto" aria-label={`Puesto ${pos!.position}`}>
            {medal}
          </span>
        )}
      </div>
      {pos ? (
        <>
          <p className="text-3xl font-display font-black tabular-nums text-ink-50 leading-none">
            #{pos.position}
            <span className="text-sm font-sans font-medium text-ink-500"> de {pos.total}</span>
          </p>
          {value && <p className="text-ink-500 text-xs mt-1.5 tabular-nums">{value}</p>}
        </>
      ) : (
        <p className="text-ink-400 text-sm leading-snug">{empty}</p>
      )}
    </div>
  );
};
